import { useState, useEffect, useCallback } from 'react';
import { TenantInfo, Provider, ProviderBranding } from '@/types/tenant';
import { 
  getCurrentDomain, 
  getDevProviderSlug 
} from '@/utils/domain';
import { computeProviderDomain, getProviderDomain } from '@/lib/provider-context';
import axios from 'axios';
import { getMockProviderBySlug, getMockProviderByDomain } from '@/mocks/providers';
import { API_BASE_URL as RESOLVED_API_BASE_URL } from '@/lib/data-source/base-url';
import { logger } from '@/lib/logger';

const USE_MOCK_PROVIDERS = import.meta.env.VITE_USE_MOCK_PROVIDERS === 'true';
const API_BASE_URL = RESOLVED_API_BASE_URL || 'https://api.florentin.dev';

const buildApiUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (!API_BASE_URL || API_BASE_URL === '/') {
    return normalizedPath;
  }

  return `${API_BASE_URL.replace(/\/+$/, '')}${normalizedPath}`;
};

// Cache for provider data to avoid repeated API calls
const providerCache = new Map<
  string,
  { provider: Provider; branding: ProviderBranding; customScripts: string | null }
>();

const toOptionalString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim().length ? value : undefined;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((entry) => String(entry)).filter((entry) => entry.length > 0);
};

function buildFallbackProvider(hostname: string, slug?: string): {
  provider: Provider;
  branding: ProviderBranding;
  customScripts: string | null;
} {
  const derivedSlug =
    slug ??
    hostname
      .split('.')
      .filter(Boolean)[0] ??
    getDevProviderSlug();

  return {
    provider: {
      id: derivedSlug,
      slug: derivedSlug,
      name: derivedSlug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      domain: hostname,
      customDomains: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    branding: {},
    customScripts: null,
  };
}

function resolveProviderDomainLookup(hostname: string, slug: string | null): string | null {
  const storedDomain = getProviderDomain();
  if (storedDomain) {
    return storedDomain;
  }

  const envOverride = import.meta.env.VITE_PROVIDER_DOMAIN_HEADER as string | undefined;
  if (envOverride && envOverride.trim().length) {
    return envOverride.trim();
  }

  return computeProviderDomain({
    provider: null,
    fallbackSlug: slug,
    fallbackHostname: hostname,
  });
}

function normalizeProviderPayload(
  payload: unknown,
  hostname: string,
  fallbackSlug?: string
): { provider: Provider; branding: ProviderBranding; customScripts: string | null } {
  const record = payload as {
    provider?: Record<string, unknown>;
    branding?: Record<string, unknown>;
    custom_scripts?: unknown;
    customScripts?: unknown;
  } | undefined;
  const providerRaw = record?.provider ?? {};
  const brandingRaw = record?.branding ?? {};
  const customScriptsRaw = record?.custom_scripts ?? record?.customScripts;
  const derivedSlug =
    toOptionalString(providerRaw.slug) ??
    fallbackSlug ??
    hostname
      .split('.')
      .filter(Boolean)[0] ??
    getDevProviderSlug();
  const nowIso = new Date().toISOString();

  return {
    provider: {
      id: String(providerRaw.id ?? derivedSlug),
      name:
        toOptionalString(providerRaw.name) ??
        toOptionalString(brandingRaw.name) ??
        derivedSlug
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
      domain: String(providerRaw.domain ?? hostname),
      slug: derivedSlug,
      customDomains: toStringArray(
        providerRaw.customDomains ?? providerRaw.custom_domains ?? providerRaw.custom_domains_list
      ),
      createdAt: String(providerRaw.createdAt ?? providerRaw.created_at ?? nowIso),
      updatedAt: String(providerRaw.updatedAt ?? providerRaw.updated_at ?? nowIso),
    },
    branding: {
      logo: toOptionalString(brandingRaw.logo_url ?? brandingRaw.logoUrl ?? brandingRaw.logo),
      logoLight: toOptionalString(brandingRaw.logo_light_url ?? brandingRaw.logoLightUrl),
      logoDark: toOptionalString(brandingRaw.logo_dark_url ?? brandingRaw.logoDarkUrl),
      primaryColor: toOptionalString(brandingRaw.primary_color ?? brandingRaw.primaryColor),
      secondaryColor: toOptionalString(
        brandingRaw.accent_color ??
          brandingRaw.accentColor ??
          brandingRaw.secondary_color ??
          brandingRaw.secondaryColor
      ),
      theme: toOptionalString(brandingRaw.theme) as ProviderBranding['theme'],
      favicon: toOptionalString(brandingRaw.favicon ?? brandingRaw.favicon_url ?? brandingRaw.faviconUrl),
      customCss: toOptionalString(brandingRaw.custom_css ?? brandingRaw.customCss),
    },
    customScripts:
      typeof customScriptsRaw === 'string' && customScriptsRaw.trim().length > 0
        ? customScriptsRaw
        : null,
  };
}

/**
 * Hook to resolve tenant information from the current domain
 */
export function useTenantFromDomain(): TenantInfo & { refreshTenant: () => Promise<void> } {
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>({
    provider: {} as Provider,
    branding: {},
    customScripts: null,
    isCustomDomain: false,
    isLoading: true,
    error: null,
  });

  const fetchProviderByDomain = useCallback(async (hostname: string) => {
    try {
      // Check cache first
      const cacheKey = hostname;
      if (providerCache.has(cacheKey)) {
        const cached = providerCache.get(cacheKey)!;
        return cached;
      }

      // Use mock data in development
      if (USE_MOCK_PROVIDERS) {
        const mockData = getMockProviderByDomain(hostname);
        if (mockData) {
          providerCache.set(cacheKey, mockData);
          return mockData;
        }
        throw new Error('Provider not found in mock data');
      }

      // For domain-based resolution, the backend handles it automatically
      // Note: This endpoint should be public (no auth required) to get provider branding
      // The backend auth guide doesn't specify this endpoint, so we may need to create it
      // or use a different approach for getting provider branding
      const providerInfoUrl = buildApiUrl('/api/v1/provider/info');

      logger.debug('[tenant] Fetching provider info', {
        url: providerInfoUrl,
        domainHeader: hostname,
      });

      const response = await axios.get(providerInfoUrl, {
        headers: {
          'X-Provider-Domain': hostname,
        },
      });

      const providerData = normalizeProviderPayload(response.data?.data ?? response.data, hostname);

      // Cache the result
      providerCache.set(cacheKey, providerData);
      
      return providerData;
    } catch (error) {
      const fallback = buildFallbackProvider(hostname);

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          logger.warn('[tenant] Provider info endpoint requires auth, falling back to inferred tenant', {
            hostname,
            status,
          });
          providerCache.set(hostname, fallback);
          return fallback;
        }
      }

      logger.error('Failed to fetch provider by domain:', error);
      providerCache.set(hostname, fallback);
      return fallback;
    }
  }, []);

  const fetchProviderBySlug = useCallback(async (slug: string) => {
    try {
      // Check cache first
      const cacheKey = `slug:${slug}`;
      if (providerCache.has(cacheKey)) {
        const cached = providerCache.get(cacheKey)!;
        return cached;
      }

      // Use mock data in development
      if (USE_MOCK_PROVIDERS) {
        const mockData = getMockProviderBySlug(slug);
        if (mockData) {
          providerCache.set(cacheKey, mockData);
          return mockData;
        }
        throw new Error('Provider not found in mock data');
      }

      // The backend doesn't have a public provider/info endpoint
      // Use fallback data since provider info will be available from auth context
      logger.debug('Using fallback provider data for tenant resolution');
      const fallbackData = buildFallbackProvider(window.location.hostname, slug);
      
      // Cache the fallback result
      providerCache.set(cacheKey, fallbackData);
      return fallbackData;
    } catch (error) {
      logger.error('Failed to resolve tenant from domain:', error);
      throw error;
    }
  }, []);

  const resolveTenant = useCallback(async () => {
    setTenantInfo(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const domainInfo = getCurrentDomain();
      
      let providerData;
      
      const slug = domainInfo.subdomain || getDevProviderSlug();
      const resolvedDomain = resolveProviderDomainLookup(domainInfo.hostname, slug);

      if (resolvedDomain) {
        providerData = await fetchProviderByDomain(resolvedDomain);
      } else {
        providerData = await fetchProviderBySlug(slug);
      }

      setTenantInfo({
        provider: providerData.provider,
        branding: providerData.branding,
        customScripts: providerData.customScripts ?? null,
        isCustomDomain: domainInfo.isCustomDomain,
        isLoading: false,
        error: null,
      });

      // Apply branding if available
      if (providerData.branding) {
        applyBranding(providerData.branding);
      }
    } catch (error) {
      const errorMessage = (error as { response?: { status: number } }).response?.status === 404
        ? 'Provider not found for this domain'
        : 'Failed to load provider information';
        
      setTenantInfo(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [fetchProviderByDomain, fetchProviderBySlug]);

  const refreshTenant = useCallback(async () => {
    // Clear cache for current domain
    const domainInfo = getCurrentDomain();
    providerCache.delete(domainInfo.hostname);
    providerCache.delete(`slug:${domainInfo.subdomain || getDevProviderSlug()}`);
    
    // Re-resolve tenant
    await resolveTenant();
  }, [resolveTenant]);

  useEffect(() => {
    resolveTenant();
  }, [resolveTenant]);

  return {
    ...tenantInfo,
    refreshTenant,
  };
}

/**
 * Apply provider branding to the document
 */
function applyBranding(branding: ProviderBranding) {
  // Portal UI should ignore branding; emails will handle it server-side.
  void branding;
}

/**
 * Hook to get current tenant info from context (to be used with TenantContext)
 */
export function useTenant() {
  // This will be implemented when we create the TenantContext
  throw new Error('useTenant must be used within TenantProvider');
}
